/**
 * Skill utilities for aggregating and processing skill-related data
 * across projects, activities, and companies for skill detail pages
 */

import { projects, activities, companies } from '../../../../../db';
import { 
  isMultiActivityProject, 
  getProjectActivityIds, 
  getPreviousActivityId, 
  getNextActivityId,
  getActivityTitle 
} from '../../projectUtils';
import { 
  calculateDurationInMonths,
  calculateProfessionalAttributedProjectDuration,
  normalizeSkillFormat,
  calculateProjectActivityIntersection
} from '../calculators/skillCalculationUtils';
import * as skillCalculationUtils from '../calculators/skillCalculationUtils';
import { SkillCategory } from '../../../../../db/skillCategories';

// Memoization cache for performance optimization
const memoizedSkillDetailsCache = new Map<string, SkillDetails | null>();
const memoizedAllSkillNamesCache = new Map<string, string[]>();
const memoizedProjectsForSkillCache = new Map<string, SkillProject[]>();

// Import types from the correct locations
interface Project {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string | null;
  activityId: number | number[] | null;
  categorizedSkills?: Record<string, Array<{ name: string; weight: number }>>;
  skills?: Array<{ name: string; weight: number }>;
  tech?: Array<{ name: string; weight: number }>;
}

interface Activity {
  id: number;
  title: string;
  startDate: string;
  endDate: string | null;
  companyId: number;
}

interface Company {
  id: number;
  name: string;
  logoPath?: string;
  logoText: string;
}

export interface SkillProject {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string | null;
  activityId: number;
  activityTitle: string;
  companyName: string;
  companyLogo?: string;
  skillWeight: number;
  duration: number; // in months
  skillSpecificPoints: number; // duration × weight for this project
  totalSkillPoints: number; // sum of all project durations for reference
  // Multi-activity project support
  isMultiActivity?: boolean;
  allActivityIds?: number[];
  allActivityTitles?: string[];
  allCompanyNames?: string[];
  isContinuation?: boolean;
  previousActivityId?: number;
  previousActivityTitle?: string;
  nextActivityId?: number;
  nextActivityTitle?: string;
}

export interface SkillActivity {
  id: number;
  title: string;
  startDate: string;
  endDate: string | null;
  companyName: string;
  companyLogo?: string;
  duration: number; // in months
  skillWeight: number;
  skillUsageProjects: number; // number of projects using this skill in this activity
}

export interface SkillCompany {
  id: number;
  name: string;
  logoPath?: string;
  logoText: string;
  totalDuration: number; // total months skill was used at this company
  averageWeight: number;
  skillSpecificPoints: number; // sum of (duration × weight) for this company's projects
  totalSkillPoints: number; // sum of all project durations for reference
  activitiesCount: number;
  projectsCount: number;
}

export interface SkillTimeline {
  startDate: string;
  endDate: string | null;
  activityTitle: string;
  companyName: string;
  companyLogo?: string;
  projectsCount: number;
  averageWeight: number;
  skillSpecificPoints: number; // sum of (duration × weight) for this timeline entry
  totalSkillPoints: number; // sum of all project durations for reference
  isOngoing: boolean;
}

export interface SkillDetails {
  name: string;
  category: SkillCategory;
  totalProjects: number;
  totalActivities: number;
  totalCompanies: number;
  totalExperience: number; // total weighted months
  averageWeight: number;
  skillSpecificPoints: number; // sum of (duration × weight) for projects using the skill
  totalSkillPoints: number; // sum of all project durations
  firstUsed: string; // earliest project date
  lastUsed: string | null; // latest project date or Present
  isCurrentlyUsed: boolean;
  projects: SkillProject[];
  activities: SkillActivity[];
  companies: SkillCompany[];
  timeline: SkillTimeline[];
}

/**
 * Calculate actual experience time for a skill, avoiding double-counting parallel projects
 */
function calculateActualExperienceMonths(skillProjects: SkillProject[]): number {
  if (skillProjects.length === 0) return 0;
  
  // Create time periods with weights
  const timePeriods: Array<{ start: Date; end: Date; weight: number }> = [];
  
  skillProjects.forEach(project => {
    const startDate = new Date(project.startDate);
    const endDate = project.endDate && project.endDate !== 'Present' 
      ? new Date(project.endDate) 
      : new Date();
    
    timePeriods.push({
      start: startDate,
      end: endDate,
      weight: project.skillWeight
    });
  });
  
  // Sort by start date
  timePeriods.sort((a, b) => a.start.getTime() - b.start.getTime());
  
  // Merge overlapping periods and calculate weighted experience
  const mergedPeriods: Array<{ start: Date; end: Date; totalWeight: number; count: number }> = [];
  
  for (const period of timePeriods) {
    let merged = false;
    
    for (const existing of mergedPeriods) {
      // Check if periods overlap
      if (period.start <= existing.end && period.end >= existing.start) {
        // Extend the existing period and accumulate weights
        existing.start = new Date(Math.min(existing.start.getTime(), period.start.getTime()));
        existing.end = new Date(Math.max(existing.end.getTime(), period.end.getTime()));
        existing.totalWeight += period.weight;
        existing.count += 1;
        merged = true;
        break;
      }
    }
    
    if (!merged) {
      mergedPeriods.push({
        start: period.start,
        end: period.end,
        totalWeight: period.weight,
        count: 1
      });
    }
  }
  
  // Calculate total weighted months
  let totalWeightedMonths = 0;
  
  for (const period of mergedPeriods) {
    const months = calculateMonthsBetween(
      period.start.toISOString().split('T')[0], 
      period.end.toISOString().split('T')[0]
    );
    
    // Use average weight for the merged period
    const averageWeight = period.totalWeight / period.count;
    totalWeightedMonths += months * averageWeight;
  }
  
  return totalWeightedMonths;
}

/**
 * Calculate duration in months between two dates
 */
function calculateMonthsBetween(startDate: string, endDate: string | null): number {
  const start = new Date(startDate);
  const end = endDate && endDate !== 'Present' ? new Date(endDate) : new Date();
  
  const yearDiff = end.getFullYear() - start.getFullYear();
  const monthDiff = end.getMonth() - start.getMonth();
  
  return Math.max(1, yearDiff * 12 + monthDiff + 1);
}

/**
 * Extract all skills from a project (handles both legacy and categorized formats)
 */
function extractSkillsFromProject(project: any): Array<{ name: string; weight: number; category: string }> {
  const skills: Array<{ name: string; weight: number; category: string }> = [];
  
  if (project.categorizedSkills) {
    // New categorized format
    Object.entries(project.categorizedSkills).forEach(([category, categorySkills]: [string, any]) => {
      if (Array.isArray(categorySkills)) {
        categorySkills.forEach((skill: { name: string; weight: number }) => {
          skills.push({ ...skill, category });
        });
      }
    });
  } else {
    // Legacy format - fallback to skills and tech arrays
    if (project.skills && Array.isArray(project.skills)) {
      project.skills.forEach((skill: any) => {
        skills.push({ ...skill, category: 'profession' });
      });
    }
    if (project.tech && Array.isArray(project.tech)) {
      project.tech.forEach((tech: any) => {
        skills.push({ ...tech, category: 'tools' });
      });
    }
  }
  
  return skills;
}

/**
 * Get all projects that use a specific skill
 * For multi-activity projects, shows them only once with continuation information
 */
export function getProjectsForSkill(skillName: string): SkillProject[] {
  // Simple cache key based on skillName and projects data length for invalidation
  const cacheKey = `projects-${skillName.toLowerCase()}-${projects.length}`;
  
  if (memoizedProjectsForSkillCache.has(cacheKey)) {
    return memoizedProjectsForSkillCache.get(cacheKey)!;
  }
  
  // Calculate total skill points from all professional projects
  const allProfessionalProjects = projects.filter((p: any) => p.activityId !== null);
  const totalSkillPoints = allProfessionalProjects.reduce((total, project: any) => {
    const attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    return total + attributedDuration;
  }, 0);
  
  const skillProjects: SkillProject[] = [];
  const processedProjectIds = new Set<number>(); // Track processed projects to avoid duplicates
  
  projects.forEach((project: any) => {
    // Skip if project already processed (for multi-activity projects)
    if (processedProjectIds.has(project.id)) {
      return;
    }

    const projectSkills = extractSkillsFromProject(project);
    const skillMatch = projectSkills.find(s => 
      s.name.toLowerCase() === skillName.toLowerCase()
    );
    
    if (skillMatch) {
      processedProjectIds.add(project.id);
      
      // Handle both single activityId and array of activityIds
      const activityIds = getProjectActivityIds(project);
      
      if (activityIds.length === 0) {
        // Private project - skip or handle separately if needed
        return;
      }

      // For multi-activity projects, use the first activity as the primary reference
      // but include information about all activities
      const isMultiActivity = isMultiActivityProject(project);
      const primaryActivityId = activityIds[0];
      const primaryActivity = activities.find((a: any) => a.id === primaryActivityId);
      const primaryCompany = companies.find((c: any) => c.id === primaryActivity?.companyId);
      
      if (primaryActivity && primaryCompany) {
        const duration = calculateMonthsBetween(project.startDate, project.endDate);
        const skillSpecificPoints = duration * skillMatch.weight;
        
        const skillProject: SkillProject = {
          id: project.id,
          title: project.title,
          description: project.description,
          startDate: project.startDate,
          endDate: project.endDate,
          activityId: primaryActivityId,
          activityTitle: primaryActivity.title,
          companyName: primaryCompany.name,
          companyLogo: primaryCompany.logoPath,
          skillWeight: skillMatch.weight,
          duration: duration,
          skillSpecificPoints: skillSpecificPoints,
          totalSkillPoints: duration // Use project's own duration for project-specific ratio
        };

        // Add multi-activity information if applicable
        if (isMultiActivity) {
          const allActivities = activityIds.map(id => activities.find((a: any) => a.id === id)).filter(Boolean);
          const allCompanies = allActivities.map(activity => 
            companies.find((c: any) => c.id === activity?.companyId)
          ).filter(Boolean);

          skillProject.isMultiActivity = true;
          skillProject.allActivityIds = activityIds;
          skillProject.allActivityTitles = allActivities.map(a => a!.title);
          skillProject.allCompanyNames = allCompanies.map(c => c!.name);
          
          // Get next activity info (since we're showing from the perspective of the first activity)
          const nextActivityId = getNextActivityId(project, primaryActivityId);
          if (nextActivityId) {
            const nextActivity = activities.find((a: any) => a.id === nextActivityId);
            if (nextActivity) {
              skillProject.nextActivityId = nextActivityId;
              skillProject.nextActivityTitle = nextActivity.title;
            }
          }
        }

        skillProjects.push(skillProject);
      }
    }
  });
  
  const result = skillProjects.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  
  // Cache the computed result
  memoizedProjectsForSkillCache.set(cacheKey, result);
  return result;
}

/**
 * Get all activities where a specific skill was used
 */
export function getActivitiesForSkill(skillName: string): SkillActivity[] {
  const skillProjects = getProjectsForSkill(skillName);
  const activityMap = new Map<number, SkillActivity>();
  
  skillProjects.forEach(project => {
    const activity = activities.find((a: any) => a.id === project.activityId);
    const company = companies.find((c: any) => c.id === activity?.companyId);
    
    if (activity && company) {
      if (activityMap.has(activity.id)) {
        const existing = activityMap.get(activity.id)!;
        existing.skillUsageProjects += 1;
        existing.skillWeight = (existing.skillWeight + project.skillWeight) / 2; // average weight
      } else {
        activityMap.set(activity.id, {
          id: activity.id,
          title: activity.title,
          startDate: activity.startDate,
          endDate: activity.endDate,
          companyName: company.name,
          companyLogo: company.logoPath,
          duration: calculateMonthsBetween(activity.startDate, activity.endDate),
          skillWeight: project.skillWeight,
          skillUsageProjects: 1
        });
      }
    }
  });
  
  return Array.from(activityMap.values())
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()); // Sort by start date descending
}

/**
 * Get all companies where a specific skill was used
 */
export function getCompaniesForSkill(skillName: string): SkillCompany[] {
  const companyMap = new Map<number, SkillCompany>();
  const processedProjects = new Set<number>(); // Track processed projects to avoid double-counting
  
  // Process raw projects to handle multi-activity projects correctly
  projects.forEach((project: any) => {
    if (project.activityId === null || processedProjects.has(project.id)) {
      return; // Skip private projects and already processed projects
    }
    
    const projectSkills = extractSkillsFromProject(project);
    const skillMatch = projectSkills.find(s => 
      s.name.toLowerCase() === skillName.toLowerCase()
    );
    
    if (!skillMatch) {
      return; // Skip projects that don't have this skill
    }
    
    processedProjects.add(project.id);
    const activityIds = getProjectActivityIds(project);
    const duration = calculateDurationInMonths(project.startDate, project.endDate);
    const skillSpecificPoints = duration * skillMatch.weight;
    
    // Find which companies this project spans
    const companiesForProject = new Set<number>();
    activityIds.forEach(activityId => {
      const activity = activities.find((a: any) => a.id === activityId);
      if (activity) {
        companiesForProject.add(activity.companyId);
      }
    });
    
    // Add skill points to each company the project spans
    companiesForProject.forEach(companyId => {
      const company = companies.find((c: any) => c.id === companyId);
      if (!company) return;
      
      if (companyMap.has(companyId)) {
        const existing = companyMap.get(companyId)!;
        existing.totalDuration += duration;
        existing.projectsCount += 1;
        existing.averageWeight = (existing.averageWeight + skillMatch.weight) / 2;
        existing.skillSpecificPoints += skillSpecificPoints;
      } else {
        const companyActivities = activities.filter((a: any) => a.companyId === companyId);
        companyMap.set(companyId, {
          id: companyId,
          name: company.name,
          logoPath: company.logoPath,
          logoText: company.logoText,
          totalDuration: duration,
          averageWeight: skillMatch.weight,
          skillSpecificPoints: skillSpecificPoints,
          totalSkillPoints: 0, // Will be calculated below
          activitiesCount: companyActivities.length,
          projectsCount: 1
        });
      }
    });
  });
  
  // Calculate company-specific total skill points for each company
  const companies_array = Array.from(companyMap.values());
  companies_array.forEach(company => {
    // For company skill ratio, the total should be the sum of durations 
    // of ALL projects at this company (not just ones using this skill)
    // This way the ratio shows: skill_weighted_time / total_company_project_time
    const companyActivities = activities.filter((a: any) => a.companyId === company.id);
    const companyActivityIds = companyActivities.map(a => a.id);
    
    // Find all projects at this company
    const allCompanyProjects = projects.filter((project: any) => {
      if (project.activityId === null) return false;
      
      if (Array.isArray(project.activityId)) {
        return project.activityId.some((id: number) => companyActivityIds.includes(id));
      }
      return companyActivityIds.includes(project.activityId);
    });
    
    // Calculate total attributed project duration for this company
    let totalCompanyProjectDuration = 0;
    allCompanyProjects.forEach((project: any) => {
      const duration = calculateDurationInMonths(project.startDate, project.endDate);
      
      // Handle multi-activity projects - attribute proportionally
      if (Array.isArray(project.activityId)) {
        const activitiesAtCompany = project.activityId.filter((id: number) => companyActivityIds.includes(id)).length;
        const totalActivities = project.activityId.length;
        
        // If ALL activities of this project are at this company, count full duration
        // Otherwise, count proportionally
        if (activitiesAtCompany === totalActivities) {
          totalCompanyProjectDuration += duration;
        } else {
          totalCompanyProjectDuration += (duration * activitiesAtCompany) / totalActivities;
        }
      } else {
        totalCompanyProjectDuration += duration;
      }
    });
    
    company.totalSkillPoints = totalCompanyProjectDuration;
  });
  
  return companies_array.sort((a, b) => b.totalDuration - a.totalDuration);
}

/**
 * Generate timeline for skill development
 */
export function getSkillTimeline(skillName: string): SkillTimeline[] {
  const skillActivities = getActivitiesForSkill(skillName);
  
  return skillActivities.map(activity => {
    // Find the actual activity data to get precise timing
    const activityData = activities.find((a: any) => a.id === activity.id);
    if (!activityData) {
      return {
        startDate: activity.startDate,
        endDate: activity.endDate,
        activityTitle: activity.title,
        companyName: activity.companyName,
        companyLogo: activity.companyLogo,
        projectsCount: activity.skillUsageProjects,
        averageWeight: activity.skillWeight,
        skillSpecificPoints: 0,
        totalSkillPoints: 0,
        isOngoing: !activity.endDate || activity.endDate === 'Present'
      };
    }
    
    // Find all projects associated with this activity
    const activityProjects = projects.filter((p: any) => {
      if (p.activityId === null) return false;
      
      if (Array.isArray(p.activityId)) {
        return p.activityId.includes(activity.id);
      }
      return p.activityId === activity.id;
    });
    
    // Calculate total project duration within this activity (using intersection)
    const totalActivityProjectDuration = activityProjects.reduce((total, project) => {
      return total + calculateProjectActivityIntersection(project, activityData);
    }, 0);
    
    // Calculate skill-specific points for this activity
    let skillSpecificPoints = 0;
    activityProjects.forEach((project: any) => {
      const skillData = extractSkillsFromProject(project).find(s => 
        s.name.toLowerCase() === skillName.toLowerCase()
      );
      
      if (skillData) {
        const intersectionDuration = calculateProjectActivityIntersection(project, activityData);
        skillSpecificPoints += intersectionDuration * skillData.weight;
      }
    });
    
    return {
      startDate: activity.startDate,
      endDate: activity.endDate,
      activityTitle: activity.title,
      companyName: activity.companyName,
      companyLogo: activity.companyLogo,
      projectsCount: activity.skillUsageProjects,
      averageWeight: activity.skillWeight,
      skillSpecificPoints: skillSpecificPoints,
      totalSkillPoints: totalActivityProjectDuration, // Use activity project total, not activity duration
      isOngoing: !activity.endDate || activity.endDate === 'Present'
    };
  });
}

/**
 * Get comprehensive skill details
 */
export function getSkillDetails(skillName: string): SkillDetails | null {
  // Simple cache key based on skillName and projects data length for invalidation
  const cacheKey = `${skillName.toLowerCase()}-${projects.length}`;
  
  if (memoizedSkillDetailsCache.has(cacheKey)) {
    return memoizedSkillDetailsCache.get(cacheKey)!;
  }
  
  const skillProjects = getProjectsForSkill(skillName);
  
  if (skillProjects.length === 0) {
    // Cache null results as well to avoid repeated expensive computations
    memoizedSkillDetailsCache.set(cacheKey, null);
    return null;
  }
  
  const skillActivities = getActivitiesForSkill(skillName);
  const skillCompanies = getCompaniesForSkill(skillName);
  const skillTimeline = getSkillTimeline(skillName);
  
  // Calculate totals and statistics
  const totalExperience = calculateActualExperienceMonths(skillProjects);
  const averageWeight = skillProjects.reduce((sum, project) => 
    sum + project.skillWeight, 0
  ) / skillProjects.length;
  
  // Calculate skill points following the same logic as live examples
  // Get all professional projects (excluding private projects with activityId === null)
  const allProfessionalProjects = projects.filter((project: any) => project.activityId !== null);
  
  // Calculate total skill points (sum of all professional project durations)
  const totalSkillPoints = allProfessionalProjects.reduce((total, project: any) => {
    const attributedDuration = calculateProfessionalAttributedProjectDuration(project);
    return total + attributedDuration;
  }, 0);
  
  // Calculate skill-specific points (sum of duration × weight for projects using this skill)
  const skillSpecificPoints = skillProjects.reduce((total, project) => {
    // Find the actual project data to get duration
    const actualProject = projects.find((p: any) => p.id === project.id);
    if (actualProject) {
      const projectDuration = calculateProfessionalAttributedProjectDuration(actualProject);
      return total + (projectDuration * project.skillWeight);
    }
    return total;
  }, 0);
  
  const sortedProjects = skillProjects.sort((a, b) => 
    new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );
  const firstUsed = sortedProjects[sortedProjects.length - 1].startDate;
  const lastUsed = sortedProjects[0].endDate;
  
  // Determine skill category (use the most common category if mixed)
  const categoryCount = new Map<string, number>();
  skillProjects.forEach(project => {
    const projectSkills = extractSkillsFromProject({ id: project.id } as any);
    const skill = projectSkills.find(s => s.name.toLowerCase() === skillName.toLowerCase());
    if (skill) {
      categoryCount.set(skill.category, (categoryCount.get(skill.category) || 0) + 1);
    }
  });
  
  const mostCommonCategory = Array.from(categoryCount.entries())
    .sort((a, b) => b[1] - a[1])[0]?.[0] as SkillCategory || 'profession';
  
  const result: SkillDetails = {
    name: skillName,
    category: mostCommonCategory,
    totalProjects: skillProjects.length,
    totalActivities: skillActivities.length,
    totalCompanies: skillCompanies.length,
    totalExperience: Math.round(totalExperience * 100) / 100,
    averageWeight: Math.round(averageWeight * 100) / 100,
    skillSpecificPoints: Math.round(skillSpecificPoints * 100) / 100,
    totalSkillPoints: Math.round(totalSkillPoints * 100) / 100,
    firstUsed,
    lastUsed,
    isCurrentlyUsed: !lastUsed || lastUsed === 'Present' || skillTimeline.some(t => t.isOngoing),
    projects: skillProjects,
    activities: skillActivities,
    companies: skillCompanies,
    timeline: skillTimeline
  };
  
  // Cache the computed result
  memoizedSkillDetailsCache.set(cacheKey, result);
  return result;
}

/**
 * Get all unique skill names across all projects
 */
export function getAllSkillNames(): string[] {
  // Simple cache key based on projects data length for invalidation
  const cacheKey = `all-skills-${projects.length}`;
  
  if (memoizedAllSkillNamesCache.has(cacheKey)) {
    return memoizedAllSkillNamesCache.get(cacheKey)!;
  }
  
  const skillNames = new Set<string>();
  
  projects.forEach((project: any) => {
    const projectSkills = extractSkillsFromProject(project);
    projectSkills.forEach(skill => {
      skillNames.add(skill.name);
    });
  });
  
  const result = Array.from(skillNames).sort();
  
  // Cache the computed result
  memoizedAllSkillNamesCache.set(cacheKey, result);
  return result;
}

/**
 * Search for skills by name (case-insensitive, partial match)
 */
export function searchSkills(query: string): string[] {
  const allSkills = getAllSkillNames();
  const lowerQuery = query.toLowerCase();
  
  return allSkills.filter(skill => 
    skill.toLowerCase().includes(lowerQuery)
  );
}

/**
 * URL-safe encoding for skill names
 * Simple, deterministic approach that handles edge cases properly
 */
export function encodeSkillName(skillName: string): string {
  // Handle special cases and normalize the skill name for URL encoding
  let encoded = skillName.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric sequences with single dash
    .replace(/^-+|-+$/g, '');     // Remove leading/trailing dashes
  
  // Ensure we don't have an empty string
  if (!encoded) {
    encoded = 'unknown-skill';
  }
  
  return encodeURIComponent(encoded);
}

/**
 * Decode URL-safe skill name back to original
 * Uses smart matching to find the correct skill from the database
 */
export function decodeSkillName(encodedName: string): string {
  // Decode and convert dashes back to spaces for initial matching
  const decodedSlug = decodeURIComponent(encodedName);
  const allSkills = getAllSkillNames();
  
  // Strategy 1: Try direct slug-to-skill mapping (exact match)
  // Convert slug back to expected format and find exact match
  const spaceConverted = decodedSlug.replace(/-/g, ' ');
  const exactMatch = allSkills.find(skill => 
    skill.toLowerCase() === spaceConverted.toLowerCase()
  );
  if (exactMatch) {
    return exactMatch;
  }
  
  // Strategy 2: Try normalized matching
  // This handles cases like "Cross-functional Collaboration" vs "cross functional collaboration"
  const normalizedMatch = allSkills.find(skill => {
    const normalizedSkill = skill.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    return normalizedSkill === spaceConverted.toLowerCase();
  });
  if (normalizedMatch) {
    return normalizedMatch;
  }
  
  // Strategy 3: Try encoding-based reverse lookup
  // Check if any skill would generate this exact slug
  const reverseMatch = allSkills.find(skill => {
    const skillSlug = skill.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return skillSlug === decodedSlug;
  });
  if (reverseMatch) {
    return reverseMatch;
  }
  
  // Strategy 4: Partial match fallback (for very fuzzy matching)
  const partialMatch = allSkills.find(skill => 
    skill.toLowerCase().includes(spaceConverted.toLowerCase()) ||
    spaceConverted.toLowerCase().includes(skill.toLowerCase())
  );
  if (partialMatch) {
    return partialMatch;
  }
  
  // If no match found, return the decoded slug (graceful degradation)
  return spaceConverted;
}

/**
 * Certification Skills Utilities
 * Functions for detecting and managing skills covered by certificates
 */

import { certificates } from '../../../../../db';

/**
 * Get all skills that are covered by visible certificates
 * @returns Set of normalized skill names that have certifications
 */
export function getCertifiedSkills(): Set<string> {
  const certifiedSkills = new Set<string>();
  
  // Get skills from all visible certificates
  if (certificates && Array.isArray(certificates)) {
    certificates
      .filter((cert) => cert.isVisible)
      .forEach((cert) => {
        if (cert.skills && Array.isArray(cert.skills)) {
          cert.skills.forEach((skill: string) => {
            // Normalize skill name for consistent matching
            certifiedSkills.add(normalizeSkillName(skill));
          });
        }
      });
  }
  
  return certifiedSkills;
}

/**
 * Normalize skill name for consistent matching across data sources
 * @param skillName - The skill name to normalize
 * @returns Normalized skill name (lowercase, trimmed)
 */
function normalizeSkillName(skillName: string): string {
  return skillName.toLowerCase().trim();
}

/**
 * Check if a specific skill is certified
 * @param skillName - The skill name to check
 * @returns true if the skill is covered by any visible certificate
 */
export function isSkillCertified(skillName: string): boolean {
  const certifiedSkills = getCertifiedSkills();
  return certifiedSkills.has(normalizeSkillName(skillName));
}

/**
 * Get certification details for a specific skill
 * @param skillName - The skill name to get certification details for
 * @returns Array of certificates that cover this skill
 */
export function getSkillCertifications(skillName: string) {
  const normalizedSkillName = normalizeSkillName(skillName);
  
  if (!certificates || !Array.isArray(certificates)) {
    return [];
  }
  
  return certificates
    .filter((cert) => 
      cert.isVisible && 
      cert.skills?.some(skill => normalizeSkillName(skill) === normalizedSkillName)
    );
}
